import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { eventBookings } from "./service";
import useTableSearch from "../../../../hooks/useTableSearch";
import Dialog from "../../../dialog/Dialog";
import Toster from "../../../common/Toster";
import Pagination from "../../../common/Pagination";
import {
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../../common/AdminPageLayout";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import {
  BookingDetailHeader,
  BookingDetailSection,
  InfoRow,
} from "../BookingDetailUi";
import {
  formatListingDate as formatDate,
  formatListingDateTime,
  formatListingTime as formatTime,
} from "../../../../utils/formatListingDate";

const EventBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // React Query hooks
  const { data: eventBookingsData } = useQuery({
    queryKey: ["eventBookings", currentPage, activeSearch],
    queryFn: () => eventBookings(currentPage, activeSearch),
  });

  const eventBookingsList = eventBookingsData?.data?.data || [];
  const pagination = {
    currentPage: eventBookingsData?.data?.current_page || 1,
    lastPage: eventBookingsData?.data?.last_page || 1,
    perPage: eventBookingsData?.data?.per_page || 10,
    total: eventBookingsData?.data?.total || 0,
    from: eventBookingsData?.data?.from || 0,
    to: eventBookingsData?.data?.to || 0,
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const getPaymentStatus = (booking) => {
    if (parseFloat(booking.balance) === 0) {
      return "Paid";
    } else if (parseFloat(booking.total_paid) > 0) {
      return "Partially Paid";
    }
    return "Unpaid";
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "Partially Paid":
        return "bg-yellow-100 text-yellow-800";
      case "Unpaid":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const selectedBooking = eventBookingsList.find(
    (booking) => booking.id === selectedBookingId
  );

  return (
    <>
      {showError && (
        <Toster
          message="An error occurred"
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageCard>
        <AdminPageCardToolbar>
          <TableSearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search event bookings..."
          />
        </AdminPageCardToolbar>

        <AdminTable className="shadow-none rounded-none">
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Event</AdminTableTh>
          <AdminTableTh>Customer</AdminTableTh>
          <AdminTableTh>Event Date</AdminTableTh>
          <AdminTableTh>Total Amount</AdminTableTh>
          <AdminTableTh>Paid Amount</AdminTableTh>
          <AdminTableTh>Balance</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {eventBookingsList.length > 0 ? (
            eventBookingsList.map((booking, index) => (
              <AdminTableRow key={booking.id}>
                <AdminTableCell>{index + 1}</AdminTableCell>
                <AdminTableCell>{booking.event?.name || "Event Name"}</AdminTableCell>
                <AdminTableCell>{booking.user?.name || "Customer Name"}</AdminTableCell>
                <AdminTableCell>
                  {formatDate(booking.event_date || booking.created_at)}
                </AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.total_amount)}</AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.total_paid)}</AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.balance)}</AdminTableCell>
                <AdminTableCell>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(
                      getPaymentStatus(booking)
                    )}`}
                  >
                    {getPaymentStatus(booking)}
                  </span>
                </AdminTableCell>
                <TableActionsCell>
                  <ViewDetailsButton onClick={() => handleViewDetails(booking)} />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={9}
              searchQuery={activeSearch}
              onClearSearch={clearSearch}
              emptyMessage="No event bookings found"
              searchMessage="No event bookings found matching your search"
            />
          )}
        </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {/* Details Dialog */}
      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Event Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-3/4"
          showCloseButton={true}
        >
          <div className="space-y-4">
            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking.event?.name || "Event"}
              meta={
                <>
                  <span className="inline-flex items-center gap-1">
                    <i className="fi fi-rr-calendar text-[10px] text-gray-400" />
                    {formatDate(selectedBooking.event_date || selectedBooking.created_at)}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="fi fi-rr-marker text-[10px] text-gray-400" />
                    {selectedBooking.event?.location || "Location N/A"}
                  </span>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(selectedBooking.total_amount)}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Booking Information */}
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Booking Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Booking ID
                    </p>
                    <p className="mt-1">#{selectedBooking.id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Event Date
                    </p>
                    <p className="mt-1">
                      {formatDate(
                        selectedBooking.event_date || selectedBooking.created_at
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Total Amount
                    </p>
                    <p className="mt-1">
                      {formatCurrency(selectedBooking.total_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Discount
                    </p>
                    <p className="mt-1">
                      {formatCurrency(selectedBooking.discount_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Paid Amount
                    </p>
                    <p className="mt-1">
                      {formatCurrency(selectedBooking.total_paid)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Balance</p>
                    <p className="mt-1">
                      {formatCurrency(selectedBooking.balance)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Ticket Count
                    </p>
                    <p className="mt-1">{selectedBooking.ticket_count || 1}</p>
                  </div>
                </div>
              </div>

              {/* Event Information */}
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Event Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Event Name
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedBooking.event?.name || "Event Name"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Location
                    </p>
                    <p className="mt-1">
                      {selectedBooking.event?.location ||
                        "Location not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Duration
                    </p>
                    <p className="mt-1">
                      {selectedBooking.event?.duration ||
                        "Duration not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Starting Date
                    </p>
                    <p className="mt-1">
                      {selectedBooking.event?.starting_date
                        ? formatDate(selectedBooking.event.starting_date)
                        : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Ending Date
                    </p>
                    <p className="mt-1">
                      {selectedBooking.event?.ending_date
                        ? formatDate(selectedBooking.event.ending_date)
                        : "Not specified"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div className="bg-white p-4 rounded-lg border md:col-span-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Payment History
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Amount
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Payment Method
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          Order ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedBooking.event_payment &&
                      selectedBooking.event_payment.length > 0 ? (
                        selectedBooking.event_payment.map((payment) => (
                          <tr key={payment.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatListingDateTime(payment.created_at)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatCurrency(payment.amount)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 capitalize">
                              {payment.payment_method}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  payment.status === "completed"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {payment.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {payment.order_id}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center text-gray-500 text-sm py-4"
                          >
                            No payment history available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* User Information */}
              <div className="bg-white p-4 rounded-lg border md:col-span-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  User Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {selectedBooking.user?.name || "User Name"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="mt-1">
                        {selectedBooking.user?.email || "Email not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="mt-1">
                        {selectedBooking.user?.phone || "Phone not specified"}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Member Since
                      </p>
                      <p className="mt-1">
                        {selectedBooking.user?.created_at_formatted ||
                          "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Account Status
                      </p>
                      <p className="mt-1">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            selectedBooking.user?.is_blocked
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {selectedBooking.user?.is_blocked
                            ? "Blocked"
                            : "Active"}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Profile Image
                      </p>
                      <div className="mt-1">
                        {selectedBooking.user?.profile_image ? (
                          <img
                            src={selectedBooking.user.profile_image}
                            alt={selectedBooking.user.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-400">No image</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          showInfo={true}
        />
      </div>
    </>
  );
};

export default EventBookings;
